import { useCallback, useState } from 'react';
import type { WorkRule, TransferItem } from 'repository/timekeeper/@types/WorkRuleModel';
import { TimekeeperService } from 'services/TimekeeperService';

const timekeeperService = new TimekeeperService();

const dataList = {
    break_rules: [] as TransferItem[],
    bonus: [] as TransferItem[],
    exception_rules: [] as TransferItem[],
    paycode_distribution: [] as TransferItem[],
    rounding_rules: [] as TransferItem[],
};

export const useWorkRulesQuery = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<WorkRule[]>([]);
    const [isSelectOpenRounding, setIsSelectOpenRounding] = useState(false);
    const [isSelectOpenExceptions, setIsSelectOpenExceptions] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [workRulesData, bonusRulesData, breakRulesData, exceptionRuleData, paycodeDistributionData, roundingRulesData] = await Promise.allSettled([
                timekeeperService.work_rules.getWorkRules(),
                timekeeperService.bonus_rules.get(),
                timekeeperService.break_rule.getBreakRules(),
                timekeeperService.exception_rules.getExceptionRules(),
                timekeeperService.paycode_distributions.getPaycodeDistributions(),
                timekeeperService.rounding_rules.getRoundingRules(),
            ]);

            if (workRulesData.status === 'fulfilled') {
                const workRules = workRulesData.value as WorkRule[];
                setRows(workRules);
            }

            if (breakRulesData.status === 'fulfilled') {
                const breakRules = breakRulesData.value.map((breakRule: any) => ({
                    id: breakRule.break_rule_id,
                    name: breakRule.name,
                }));
                dataList.break_rules = breakRules;
            }

            if (bonusRulesData.status === 'fulfilled') {
                const bonusRules = bonusRulesData.value.map((bonusRule: any) => ({
                    id: bonusRule.bonus_rule_id,
                    name: bonusRule.name,
                }));
                dataList.bonus = bonusRules;
            }

            if (exceptionRuleData.status === 'fulfilled') {
                const exceptionRules = exceptionRuleData.value.map((exceptionRule: any) => ({
                    id: exceptionRule.exception_rule_id,
                    name: exceptionRule.name,
                }));
                dataList.exception_rules = exceptionRules;
            }

            if (paycodeDistributionData.status === 'fulfilled') {
                const paycodeDistributions = paycodeDistributionData.value.map((paycodeDistribution: any) => ({
                    id: paycodeDistribution.paycodes_distribution_id,
                    name: paycodeDistribution.name,
                }));
                dataList.paycode_distribution = paycodeDistributions;
            }

            if (roundingRulesData.status === 'fulfilled') {
                const roundingRules = roundingRulesData.value.map((roundingRule: any) => ({
                    id: roundingRule.rounding_rule_id,
                    name: roundingRule.name,
                }));
                dataList.rounding_rules = roundingRules;
            }
        } catch (error) {
            console.error('Error loading work rules:', error);
            setLoading(false);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        rows,
        loading,
        setRows,
        dataList,
        loadData,
        setLoading,
        isSelectOpenRounding,
        setIsSelectOpenRounding,
        isSelectOpenExceptions,
        setIsSelectOpenExceptions,
    };
};
